import React, { useMemo } from "react";

import { Grid } from "@mui/material";
import { Stack } from "@mui/system";
import { Constants as GridConstants } from "Components/AppGrid/shipeezi-new-grid";
import { useTranslation } from "react-i18next";
import { IoIosArrowDown, IoIosArrowUp } from "react-icons/io";

import { Booking } from "../../../../../models/booking";
import { CartonView } from "../../../../../models/carton";
import { BookingStatus } from "../../../../../util/enums";
import useStyles, { SSCCTitle } from "../../styles";
import { ContainerModalHandles } from "../AddContainerModal";
import { ConfirmReceiveSSCCHandles } from "../ConfirmReceiveSSCC";
import { CommentModalHandles } from "../CreateCommentModal";
import AddProductButton from "./AddProductButton";
import CloseButton from "./CloseButton";
import CommentButton from "./CommentButton";
import { CommentsHistoryModalHandles } from "./CommentsHistoryModal";
import { CommentCount } from "./CommentsHistoryModal/styles";
import ContainerButton from "./ContainerButton";
import DeleteButton from "./DeleteButton";
import PrintButton from "./PrintButton";
import ReceiveButton from "./ReceiveButton";
import StatusBadge from "./StatusBadge";

interface Props {
  value?: string;
  row?: CartonView;
  collapseds: string[];

  confirmReceiveRef?: React.RefObject<ConfirmReceiveSSCCHandles>;
  containerModalRef?: React.RefObject<ContainerModalHandles>;
  commentModalRef?: React.RefObject<CommentModalHandles>;
  commentsHistoryRef?: React.RefObject<CommentsHistoryModalHandles>;

  viewSupplier?: boolean;
  calledFromBooking: boolean;
  viewOnly?: boolean;
  loadingProducts?: boolean;
  loadingPrintingLabel?: boolean;
  booking?: Booking;
  calledFromShipment?: boolean;

  toggleCollapse: (sscc?: string) => void;
  addProductIntoCarton?: (sscc?: string, orderToFilter?: string) => void;
  handlePrintLabel?: (sscc?: string) => void;
  closeCarton?: (sscc?: string) => void;
  deleteCarton?: (sscc?: string) => void;
}

const RowSSCC = (props: Props) => {
  const {
    value,
    row,
    collapseds,
    toggleCollapse,
    viewOnly,
    viewSupplier,
    loadingProducts,
    calledFromBooking,
    booking,
    addProductIntoCarton,
    confirmReceiveRef,
    handlePrintLabel,
    loadingPrintingLabel,
    closeCarton,
    deleteCarton,
    containerModalRef,
    calledFromShipment,
    commentModalRef,
    commentsHistoryRef,
  } = props;

  const classes = useStyles();
  const { t: translate } = useTranslation();

  const isBookingCancelled = useMemo(() => BookingStatus.CANCELLED === booking?.statusShipment, []);
  const isBookingPending = useMemo(() => BookingStatus.PENDING === booking?.statusShipment, []);
  const container = useMemo(
    () =>
      booking?.containers?.find(
        (container) => container?.id && container?.id === row?.shipmentContainerInfoId
      )?.container ||
      row?.container ||
      "",
    [booking, row]
  );

  const LeftContent = (
    <React.Fragment>
      <StatusBadge
        carton={row}
        isBookingCancelled={isBookingCancelled}
        showOpenStatus={!!calledFromShipment && !booking?.bookingId}
      />
    </React.Fragment>
  );

  const RightContent = (
    <React.Fragment>
      {addProductIntoCarton && (
        <AddProductButton
          carton={row}
          show={viewSupplier && (calledFromBooking || calledFromShipment) && !viewOnly}
          loadingProducts={loadingProducts}
          booking={booking}
          addProductIntoCarton={addProductIntoCarton}
          isCalledFromBooking={calledFromBooking}
        />
      )}
      {confirmReceiveRef && (
        <ReceiveButton
          carton={row}
          show={(calledFromShipment || calledFromBooking) && !viewOnly && booking?.isBooking}
          confirmReceiveRef={confirmReceiveRef}
        />
      )}
      {containerModalRef && (
        <ContainerButton
          carton={row}
          show={!viewOnly && calledFromShipment}
          containerRef={containerModalRef}
        />
      )}
      {!!handlePrintLabel && (
        <PrintButton
          carton={row}
          show={
            (calledFromShipment || calledFromBooking) && !isBookingPending && !isBookingCancelled
          }
          handlePrintLabel={handlePrintLabel}
          loadingPrintLabel={!!loadingPrintingLabel}
        />
      )}
      {closeCarton && (
        <CloseButton
          carton={row}
          show={!viewOnly && viewSupplier && (calledFromShipment || calledFromBooking)}
          closeCarton={closeCarton}
        />
      )}
      {commentModalRef && (
        <CommentButton
          carton={row}
          show={!viewOnly && viewSupplier && (calledFromShipment || calledFromBooking)}
          commentRef={commentModalRef}
        />
      )}
      {deleteCarton && (
        <DeleteButton
          carton={row}
          show={
            !viewOnly &&
            viewSupplier &&
            (calledFromShipment || calledFromBooking) &&
            !booking?.bookingId
          }
          deleteCarton={deleteCarton}
        />
      )}
    </React.Fragment>
  );

  const renderArrowByCollapse = (sscc?: string) => {
    const collapsed = collapseds.find((item) => item === sscc);

    if (collapsed) {
      return <IoIosArrowDown color="#667085" size={GridConstants.defaultRowActionSize} />;
    }

    return <IoIosArrowUp color="#667085" size={GridConstants.defaultRowActionSize} />;
  };
  const commmentsCount = row?.comments?.length;

  return (
    <Grid
      container
      direction="row"
      justifyContent="space-between"
      data-cy="container"
      alignItems="center"
    >
      <Grid item data-cy="sscc" xs={6} container direction="row" alignItems="center">
        <Stack>
          <SSCCTitle>{value}</SSCCTitle>
          {!!container && <span>{`${translate("addedToContainer")} #${container}`}</span>}
          {!!commmentsCount && (
            <CommentCount
              key={commmentsCount}
              onClick={() => commentsHistoryRef?.current?.open(row?.sscc, row?.comments)}
            >{`${commmentsCount} ${translate(
              `comment${commmentsCount > 1 ? "s" : ""}`
            )}`}</CommentCount>
          )}
        </Stack>
        {LeftContent}
      </Grid>
      <Grid
        item
        container
        direction="row"
        data-cy="container-2"
        alignItems="center"
        justifyContent="flex-end"
        xs={6}
        wrap="nowrap"
      >
        {RightContent}
        <Grid
          item
          className={classes.actionButton}
          onClick={() => toggleCollapse(value)}
          data-cy="collapse"
        >
          {renderArrowByCollapse(row?.sscc)}
        </Grid>
      </Grid>
    </Grid>
  );
};

export default RowSSCC;
